import type { ReactElement } from 'react';
import type { StatusPresentation, StatusShape, StatusTone } from '@fleet/shared';

/**
 * A status is a shape, a word and a colour — in that order of importance.
 *
 * Remove the colour and it still reads. That is the requirement: a colourblind
 * staff member has to be able to work a full shift.
 */

const TONE_CLASSES: Readonly<Record<StatusTone, string>> = {
  neutral: 'text-ink-muted border-rule-strong',
  active: 'text-signal border-signal/40 bg-signal-wash',
  attention: 'text-attention border-attention/40 bg-attention-wash',
  alert: 'text-alert border-alert/40 bg-alert-wash',
  done: 'text-ink-muted border-rule bg-paper',
};

function Glyph({ shape }: { shape: StatusShape }): ReactElement {
  const stroke = 'currentColor';
  switch (shape) {
    case 'square':
      return (
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <rect x="1" y="1" width="8" height="8" fill={stroke} />
        </svg>
      );
    case 'dot':
      return (
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <circle cx="5" cy="5" r="4" fill={stroke} />
        </svg>
      );
    case 'ring':
      return (
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <circle cx="5" cy="5" r="3.5" fill="none" stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case 'bar':
      return (
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <rect x="0" y="3.5" width="10" height="3" fill={stroke} />
        </svg>
      );
    case 'cross':
      return (
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <path d="M1 1 9 9M9 1 1 9" stroke={stroke} strokeWidth="1.75" fill="none" />
        </svg>
      );
  }
}

export function StatusPill({ status }: { status: StatusPresentation }): ReactElement {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5',
        'text-micro font-medium uppercase',
        TONE_CLASSES[status.tone],
      ].join(' ')}
    >
      <Glyph shape={status.shape} />
      {status.label}
    </span>
  );
}
